import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, type CurrentListingPatch, type CurrentListingRow, type CurrentListingsResponse } from '../lib/api';
import Layout from '../components/Layout';
import { Dialog } from '../components/ui/Dialog';

/**
 * LISTINGS-AI - his Current Listings: each listing, and the exact words GoHighLevel's agents are given about it.
 * ⭐ paperwork_owed, confirm_stale, kb and kb_error are the SERVER's decisions (currentListingsService.ts); this screen
 *    shows them and never re-derives them. kb_error / sync.last_error arrive already plain and are shown verbatim.
 * ⛔ A refusal is said BESIDE its control, in plain words (the 111th) - never a toast, never a raw status.
 * ⭐ Each listing is a card, so it reads on his iPhone at 390 px: every row wraps, long Q&A text wraps.
 */
type Row = CurrentListingRow;
type Listings = Omit<CurrentListingsResponse, 'success'>;
/** `confirmed` puts the row the server answered on screen; `edited` does that and re-reads the list (the summary names prices). */
type Saved = { confirmed: (row: Row) => void; edited: (row: Row) => void };

const GROUPS: Array<{ key: Row['status']; title: string }> = [{ key: 'active', title: 'Active' }, { key: 'in_escrow', title: 'In escrow' }];
const CONTACTS: Record<Row['contact'], string> = { robert: 'Robert Mendieta (951) 977-3251', joseph: 'Joseph Lombera (909) 406-6538' };
const SYNC_OFF = 'Not sent to GoHighLevel yet - the sync is off';
const ADMIN_CONFIRM = 'Only an admin can confirm this listing.';
const ADMIN_CHANGE = 'Only an admin can change this listing.';

/** The Los Angeles calendar day of a stamp, with its year - a fixed zone, so no browser zone can move the day. */
export const confirmedDay = (at: string): string =>
  new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Los_Angeles' });

const statusOf = (e: unknown): number | null => {
  const s = typeof e === 'object' && e !== null ? (e as { status?: unknown }).status : undefined;
  return typeof s === 'number' ? s : null;
};
/** A 403 names who may act; the schema's bare "Validation failed" is said plainly; anything else is the server's own sentence. */
const refusal = (e: unknown, adminOnly: string, fallback: string): string => {
  const status = statusOf(e);
  if (status === 403) return adminOnly;
  const message = e instanceof Error ? e.message : '';
  if (status === 400 && message === 'Validation failed') return 'Not saved - one of the values is not one a listing can hold.';
  return message || fallback;
};

const sizes = (r: Row): string[] => [
  ...(r.size_sf !== null ? [`${r.size_sf.toLocaleString('en-US')} SF`] : []),
  ...(r.lot_acres !== null ? [`${r.lot_acres.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${r.lot_acres === 1 ? 'acre' : 'acres'}`] : []),
];

/** Where the row's Q&A stands, as the server decided it (`kb`), and the words themselves. */
function WhatGoHighLevelSays({ r }: { r: Row }) {
  const state = r.kb === 'in_ghl' ? 'In GoHighLevel'
    : r.kb === 'not_yet' ? 'Not in GoHighLevel yet'
    : r.kb === 'not_sent' ? `Not told to callers${r.status === 'in_escrow' ? ' (in escrow)' : r.status === 'ended' ? ' (ended)' : ''}`
    : null;
  return (
    <div className="mt-2 min-w-0 rounded border border-gray-200 bg-gray-50 p-2">
      <p className="text-xs font-semibold text-gray-500">What GoHighLevel says</p>
      {state !== null
        ? <p className="text-gray-600">{state}</p>
        : <p className="break-words text-primary-600">{r.kb_error ?? 'Not in GoHighLevel - no reason was given'}</p>}
      {r.faq && (
        <>
          <p className="mt-1 break-words font-medium">{r.faq.question}</p>
          <p className="break-words">{r.faq.answer}</p>
        </>
      )}
    </div>
  );
}

function ListingCard({ r, saved }: { r: Row; saved: Saved }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const confirm = async () => {
    setBusy(true); setError(null);
    try { saved.confirmed((await api.confirmCurrentListing(r.id)).listing); }
    catch (e) { setError(refusal(e, ADMIN_CONFIRM, 'Could not confirm the listing')); }
    finally { setBusy(false); }
  };
  // A save that went through supersedes a refusal shown here earlier - it no longer describes the card.
  const edited = (row: Row) => { setError(null); saved.edited(row); };
  const shownSizes = sizes(r);
  return (
    <li data-testid="listing-card" className="min-w-0 rounded-lg border border-gray-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="break-words font-semibold">{r.address}</h3>
          {r.city && <p className="text-gray-600">{r.city}</p>}
        </div>
        {r.paperwork_owed && <span className="rounded bg-warning-wash px-2 text-xs text-warning">Paperwork owed</span>}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="rounded bg-gray-100 px-2 text-xs">{r.deal === 'lease' ? 'For lease' : 'For sale'}</span>
        {r.price_text ? <span className="break-words">{r.price_text}</span> : <span className="text-gray-500">Price not stated</span>}
        {shownSizes.length ? shownSizes.map((s) => <span key={s}>{s}</span>) : <span className="text-gray-500">Size not stated</span>}
        {r.property_kind && <span className="text-gray-600">{r.property_kind}</span>}
      </div>
      {r.public_note && <p className="mt-1 break-words text-gray-700">{r.public_note}</p>}
      <p className="mt-1 text-gray-600">{`Call-back: ${CONTACTS[r.contact]}`}</p>
      <p className="mt-1 flex flex-wrap items-center gap-2">
        {r.confirm_stale
          ? <>
              <span className="rounded bg-warning-wash px-2 text-warning">Not confirmed in 30 days</span>
              {r.confirmed_at && <span className="text-gray-600">{`last confirmed ${confirmedDay(r.confirmed_at)}`}</span>}
            </>
          : r.confirmed_at && <span>{`Confirmed ${confirmedDay(r.confirmed_at)}`}</span>}
      </p>
      <WhatGoHighLevelSays r={r} />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" disabled={busy} onClick={() => void confirm()} className="rounded bg-primary-600 px-3 py-1 text-white disabled:opacity-50">It's right - confirm</button>
        <button type="button" onClick={() => setEditing(true)} className="rounded border border-gray-300 px-3 py-1">Edit</button>
        {error && <p className="break-words text-primary-500">{error}</p>}
      </div>
      {editing && <EditListingDialog r={r} onClose={() => setEditing(false)} onSaved={edited} />}
    </li>
  );
}

type Form = { status: Row['status']; price: string; size: string; acres: string; note: string; contact: Row['contact'] };
type FieldErrors = Partial<Record<'price' | 'size' | 'acres' | 'note', string>>;

// Bounded to what the columns hold (backend validate.ts: integer 1..2147483647; numeric(10,2) 0.01..99999999.99).
const MAX_SF = 2147483647;
const MIN_ACRES = 0.01;
const MAX_ACRES = 99999999.99;
/** Whole square feet, optionally grouped "15,500" - never a sign, a fraction or a letter. */
const SIZE_PATTERN = /^(\d{1,3}(,\d{3})+|\d+)$/;
const ACRES_PATTERN = /^(\d+(\.\d+)?|\.\d+)$/;
const SIZE_WORDS = 'Enter the size as a whole number of square feet (1 or more), or leave it blank.';
const ACRES_WORDS = 'Enter the lot in acres (0.01 or more), or leave it blank.';

const textOrNull = (s: string): string | null => s.trim() || null;

/**
 * The PATCH body - only what changed - or the refusals, each said beside its field before any call. Text is compared
 * trimmed on both sides, so spaces alone (his, or an imported row's) are never a change.
 */
const readForm = (r: Row, f: Form): { patch: CurrentListingPatch; errors: FieldErrors } => {
  const patch: CurrentListingPatch = {};
  const errors: FieldErrors = {};
  if (f.status !== r.status) patch.status = f.status;
  if (f.contact !== r.contact) patch.contact = f.contact;

  const price = textOrNull(f.price);
  if (price && price.length > 200) errors.price = 'The price can be at most 200 characters.';
  else if (price !== textOrNull(r.price_text ?? '')) patch.price_text = price;

  const note = textOrNull(f.note);
  if (note && note.length > 300) errors.note = 'The note can be at most 300 characters.';
  else if (note !== textOrNull(r.public_note ?? '')) patch.public_note = note;

  const sizeText = f.size.trim();
  const size = sizeText ? Number(sizeText.replace(/,/g, '')) : null;
  if (size !== null && (!SIZE_PATTERN.test(sizeText) || size < 1)) errors.size = SIZE_WORDS;
  else if (size !== null && size > MAX_SF) errors.size = 'That size is too large - at most 2,147,483,647 SF.';
  else if (size !== r.size_sf) patch.size_sf = size;

  const acresText = f.acres.trim();
  const acres = acresText ? Number(acresText) : null;
  if (acres !== null && (!ACRES_PATTERN.test(acresText) || acres < MIN_ACRES)) errors.acres = ACRES_WORDS;
  else if (acres !== null && acres > MAX_ACRES) errors.acres = 'That lot is too large - at most 99,999,999.99 acres.';
  else if (acres !== r.lot_acres) patch.lot_acres = acres;

  return { patch, errors };
};

function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium">{label}</label>
      {children}
      {error && <p id={`${id}-error`} className="text-sm text-primary-500">{error}</p>}
    </div>
  );
}

const INPUT = 'w-full min-w-0 rounded border border-gray-300 bg-white px-2 py-1 text-sm';

function EditListingDialog({ r, onClose, onSaved }: { r: Row; onClose: () => void; onSaved: (row: Row) => void }) {
  const id = useId();
  const [form, setForm] = useState<Form>({
    status: r.status, price: r.price_text ?? '', size: r.size_sf !== null ? String(r.size_sf) : '',
    acres: r.lot_acres !== null ? String(r.lot_acres) : '', note: r.public_note ?? '', contact: r.contact,
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof Form>(k: K) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value as Form[K] }));
  const described = (k: keyof FieldErrors) => (errors[k] ? `${id}-${k}-error` : undefined);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const { patch, errors: found } = readForm(r, form);
    setErrors(found); setSaveError(null);
    if (Object.keys(found).length > 0) return;
    // ⭐ An unchanged form sends nothing.
    if (Object.keys(patch).length === 0) { onClose(); return; }
    setBusy(true);
    try {
      const { listing } = await api.updateCurrentListing(r.id, patch);
      onClose();
      onSaved(listing);
    } catch (err) {
      setSaveError(refusal(err, ADMIN_CHANGE, 'Could not save the listing'));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Edit ${r.address}`}
      description="Only what you change is saved."
      size="md"
      onSubmit={(e) => void save(e)}
      footer={
        <>
          {saveError && <p className="min-w-0 flex-1 self-center break-words text-sm text-primary-500">{saveError}</p>}
          <button type="button" onClick={onClose} className="rounded border border-gray-300 px-3 py-1">Cancel</button>
          <button type="submit" disabled={busy} className="rounded bg-primary-600 px-3 py-1 text-white disabled:opacity-50">Save</button>
        </>
      }
    >
      <div className="space-y-3">
        <Field id={`${id}-status`} label="Status">
          <select id={`${id}-status`} value={form.status} onChange={set('status')} className={INPUT}>
            <option value="active">Active</option><option value="in_escrow">In escrow</option><option value="ended">Ended</option>
          </select>
        </Field>
        <Field id={`${id}-price`} label="Price" error={errors.price}>
          <input id={`${id}-price`} value={form.price} onChange={set('price')} maxLength={200} aria-describedby={described('price')} className={INPUT} />
        </Field>
        <Field id={`${id}-size`} label="Size (SF)" error={errors.size}>
          <input id={`${id}-size`} inputMode="numeric" value={form.size} onChange={set('size')} aria-describedby={described('size')} className={INPUT} />
        </Field>
        <Field id={`${id}-acres`} label="Lot (acres)" error={errors.acres}>
          <input id={`${id}-acres`} inputMode="decimal" value={form.acres} onChange={set('acres')} aria-describedby={described('acres')} className={INPUT} />
        </Field>
        <Field id={`${id}-note`} label="Public note" error={errors.note}>
          <textarea id={`${id}-note`} rows={3} value={form.note} onChange={set('note')} maxLength={300} aria-describedby={described('note')} className={INPUT} />
        </Field>
        <Field id={`${id}-contact`} label="Call-back">
          <select id={`${id}-contact`} value={form.contact} onChange={set('contact')} className={INPUT}>
            <option value="robert">{CONTACTS.robert}</option><option value="joseph">{CONTACTS.joseph}</option>
          </select>
        </Field>
      </div>
    </Dialog>
  );
}

function Group({ title, rows, saved }: { title: string; rows: Row[]; saved: Saved }) {
  return (
    <section aria-label={title} className="space-y-2">
      <h2 className="font-semibold">{`${title} (${rows.length})`}</h2>
      {rows.length ? <ul className="space-y-2">{rows.map((r) => <ListingCard key={r.id} r={r} saved={saved} />)}</ul>
        : <p className="text-sm text-gray-500">None in this group.</p>}
    </section>
  );
}

export default function CurrentListings() {
  const [data, setData] = useState<Listings | null>(null);
  const [failed, setFailed] = useState(false);
  const [showEnded, setShowEnded] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  // `quiet`: a re-read after a save - a failure keeps what is on screen, never "Could not load".
  const load = useCallback(async (quiet = false) => {
    try {
      const { listings, summary, sync } = await api.listCurrentListings();
      if (mounted.current) { setData({ listings, summary, sync }); setFailed(false); }
    } catch { if (mounted.current && !quiet) setFailed(true); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const saved = useMemo<Saved>(() => {
    const confirmed = (row: Row) => setData((d) => (d ? { ...d, listings: d.listings.map((x) => (x.id === row.id ? row : x)) } : d));
    return { confirmed, edited: (row: Row) => { confirmed(row); void load(true); } };
  }, [load]);

  const ended = (data?.listings ?? []).filter((r) => r.status === 'ended');
  return (
    <Layout>
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">Current Listings</h1>
        <p className="text-sm text-gray-600">Each listing, and the exact words GoHighLevel gives callers about it.</p>
      </div>
      {failed ? <p className="text-sm text-gray-600">Could not load the listings.</p>
        : data === null ? <p className="text-sm text-gray-500">Loading the listings…</p>
        : (
          <>
            {!data.sync.enabled
              ? <p className="rounded bg-warning-wash px-3 py-2 text-sm text-warning">{SYNC_OFF}</p>
              : data.sync.last_error && <p className="break-words rounded border border-gray-200 px-3 py-2 text-sm text-primary-600">{data.sync.last_error}</p>}
            {data.listings.length === 0 ? <p className="text-sm text-gray-600">None yet - no current listings.</p> : (
              <>
                {GROUPS.map((g) => <Group key={g.key} title={g.title} rows={data.listings.filter((r) => r.status === g.key)} saved={saved} />)}
                {ended.length > 0 && (
                  <div className="space-y-2">
                    <button type="button" aria-expanded={showEnded} onClick={() => setShowEnded((s) => !s)} className="rounded border border-gray-300 px-3 py-1 text-sm">
                      {`${showEnded ? 'Hide' : 'Show'} ended (${ended.length})`}
                    </button>
                    {showEnded && <Group title="Ended" rows={ended} saved={saved} />}
                  </div>
                )}
              </>
            )}
            <section aria-label="The whole list" className="space-y-1 rounded-lg border border-gray-200 bg-white p-3 text-sm">
              <h2 className="font-semibold">What GoHighLevel says - the whole list</h2>
              <p className="break-words font-medium">{data.summary.question}</p>
              <p className="break-words">{data.summary.answer}</p>
            </section>
          </>
        )}
    </div>
    </Layout>
  );
}
